import { useMutation } from '@apollo/client';
import { css } from '@emotion/react';
import { navigate } from '@reach/router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from 'react-loader-spinner';

import colors from 'lib/colors';
import { createCpuGameMutation } from 'pages/Lobby/operations';

import { useMutationErrorHandler } from 'utils/mutation-utils';
import { BorderlessButton, Box, Button } from '../../../../../components';
import {
  CreateCpuGame,
  CreateCpuGameVariables,
  GameDifficulty,
  GameTypeEnum,
  OnlineOpponent,
} from '../../../../../graphql/types';
import useMatchmaking from '../../../hooks/useMatchmaking';
import InviteUser from './InviteUser/InviteUser';
import StartGameButtons from './StartGameButtons';

interface Props {
  selectedSceneId?: string;
  disabled?: boolean;
  gameType: GameTypeEnum;
  gameDifficulty: GameDifficulty;
  isMediumGameActive?: boolean;
}

const MatchmakingButton = ({
  selectedSceneId,
  disabled,
  gameType,
  gameDifficulty,
  isMediumGameActive
}: Props) => {
  const {
    startMatchmaking,
    stopMatchmaking,
    loading,
    error,
    data,
    allowCpuMatch,
    opponent,
  } = useMatchmaking({ gameDifficulty });

  const handleError = useMutationErrorHandler();

  const [createCpuGame, { loading: creating }] = useMutation<
    CreateCpuGame,
    CreateCpuGameVariables
  >(createCpuGameMutation, {
    onError: handleError,
  });

  const [t] = useTranslation();

  useEffect(() => {
    if (error != null) {
      handleError(error);
      stopMatchmaking();
    }
  }, [error, stopMatchmaking, handleError]);

  useEffect(() => {
    if (data != null) {
      navigate(`/game/${data.findNewGame.id}`, {
        state: { pathname: window.location.pathname },
      });
    }
  }, [data]);

  const handleStartMatchmaking = (opponent?: OnlineOpponent) => {
    startMatchmaking(selectedSceneId, opponent, gameType);
  };

  const startCpuGame = async () => {
    stopMatchmaking();
    const result = await createCpuGame({
      variables: {
        input: {
          sceneId: selectedSceneId,
          gameType,
        },
      },
    });
    if (result?.data != null) {
      navigate(`/game/${result.data.createCpuGame.id}`, {
        state: { pathname: window.location.pathname },
      });
    }
  };

  return (
    <Box css={{ zIndex: 1 }}>
      {loading && opponent == null && allowCpuMatch ? (
        <div
          css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
          `}
        >
          <p
            css={css`
              position: absolute;
              font-size: 14px;
              color: ${colors.lobbyText};
              top: -2.5em;
              white-space: nowrap;
              left: 50%;
              transform: translateX(-50%);
            `}
          >
            <Loader
              type="TailSpin"
              color={colors.lobbyText}
              height={14}
              width={14}
              css={{ display: 'inline-block', marginRight: 8 }}
            />
            {t('lobby.playWithCpuSubtext', 'Stále hledám živého protihráče')}
          </p>
          <Button
            labelT={
              gameType === GameTypeEnum.TRAINING_GAME
                ? {
                  key: 'lobby.playWithCpu',
                  defaultValue: 'Trénovat s počítačem',
                }
                : {
                  key: 'lobby.trainWithCpu',
                  defaultValue: 'Hrát s počítačem',
                }
            }
            variant="warning"
            onClick={startCpuGame}
            disabled={disabled || creating}
          />
        </div>
      ) : loading ? (
        <Button
          label={
            <>
              <Loader
                type="TailSpin"
                color="black"
                height={14}
                width={14}
                css={{ display: 'inline-block', marginRight: 8 }}
              />
              {opponent == null
                ? t('lobby.waitingForOpponent', 'Čekám na vhodného protihráče')
                : t('lobby.waitingForInvitedOpponent', {
                  defaultValue: 'Čekám na {{opponent}}',
                  opponent: opponent.user.name,
                })}
            </>
          }
          variant="warningFaded"
        />
      ) : (
        <StartGameButtons
          disabled={!!disabled}
          creating={creating}
          gameType={gameType}
          selectedSceneId={selectedSceneId}
          startCpuGame={startCpuGame}
          isMediumGameActive={isMediumGameActive}
          handleStartMatchmaking={handleStartMatchmaking}
          gameDifficulty={gameDifficulty}
        />
      )}
      {loading ? (
        <BorderlessButton
          labelT={{
            key: 'lobby.cancelMatchmaking',
            defaultValue: 'Zrušit čekání',
          }}
          onClick={stopMatchmaking}
        />
      ) : (
        isMediumGameActive && <Box mt={3}>
          <InviteUser
            selectedSceneId={selectedSceneId}
            gameType={gameType}
            onOpponentSelected={handleStartMatchmaking}
            gameDifficulty={gameDifficulty}
          />
        </Box>
      )}
    </Box>
  );
};

export default MatchmakingButton;
